import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import Toggle from "@/app/components/ui/Toggles";
import { useBOMSetupListAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMSetupListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { FaProductHunt } from "react-icons/fa6";
import { useNavigate } from "react-router";

const tableHeader = [
  { item: "Name" },
  { item: "Product(s)" },
  { item: "Quantity" },
  { item: "Status" },
  { item: "Action" },
];

const BOMSetupListing = () => {
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState({});
  const { execute: bomSetupList, bomList } = useBOMSetupListAPI();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { NO_APPOINTMENTS } = useImageConstants();

  const isBOMListInProgress = useLoader("isBOMSetupListLoadingInProgress");

  useEffect(() => {
    bomSetupList(searchQuery);
  }, [searchQuery]);

  const handelBomEdit = (bom_id: string) => {
    navigate(`/app/inventory/bom-onboard?page_step=general&&bom_id=${bom_id}`);
  };

  const handleToggleShowAll = (bomId: string) => {
    setShowAll((prev) => ({ ...prev, [bomId]: !prev[bomId] }));
  };

  const handelBomCreation = () => {
    navigate("/app/inventory/bom-onboard?page_step=general");
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-10">
      <div className="flex flex-row items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-sm sm:text-base font-semibold leading-6 text-gray-900 whitespace-nowrap">
            Listed BOM
          </h1>

          <p className="mt-2 text-sm text-gray-700 hidden sm:block">
            A list of all the BOMs for the inventories.
          </p>
        </div>

        <SolidButton size="2XL" onButtonClick={handelBomCreation}>
          Add BOM
        </SolidButton>
      </div>

      <div className="mt-3">
        <InputWithAddOn
          onEnterKeyPress={(query: string) => setSearchQuery(query)}
          containerClassName="w-full sm:w-1/2 lg:w-1/3"
          addon={<FaProductHunt className="w-4 h-4" />}
          placeholder="Search BOM"
        />
      </div>

      <div className="mt-8 flow-root">
        {isBOMListInProgress ? (
          <TableSkeleton />
        ) : bomList?.length === 0 ? (
          <NoDataFoundComponent
            imageURL={NO_APPOINTMENTS}
            message="No Data Found."
          />
        ) : (
          <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
            <div className="scroll-inner !h-auto max-h-[600px]">
              <div className="w-full">
                <div className="inline-block min-w-full align-middle">
                  <div className="bg-white rounded-lg">
                    <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                      
                      <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                        <tr>
                          {tableHeader?.map((key, index) => (
                            <th
                              key={index}
                              scope="col"
                              className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                            >
                              {key?.item}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="overflow-hidden rounded-xl w-full">
                        {bomList?.map((bom, idx) => {
                          const products =
                            bom?.general_details?.product_component || [];

                          const shouldShowAll = showAll[bom?.bom_id];

                          const productsToRender = shouldShowAll
                            ? products
                            : products.slice(0, 3);

                          const isFirst = idx === 0;
                          const isLast = idx === bomList.length - 1;
                          const isSingle = bomList.length === 1;

                          return (
                            <tr
                              key={bom?.bom_id}
                              className={clsx(
                                "group bg-white transition",
                                isSingle && "rounded-xl",
                                isFirst && !isSingle && "rounded-t-xl",
                                isLast && !isSingle && "rounded-b-xl",
                              )}
                            >
                              <td
                                className={clsx(
                                  "px-3 py-3 text-xs font-normal",
                                  isSingle &&
                                    "rounded-tl-2xl rounded-bl-2xl",
                                  isFirst &&
                                    !isSingle &&
                                    "rounded-tl-2xl",
                                  isLast &&
                                    !isSingle &&
                                    "rounded-bl-2xl",
                                )}
                              >
                                <p className="font-normal text-black text-xs">
                                  {
                                    bom?.general_details?.service_details
                                      ?.bom_name
                                  }
                                </p>
                              </td>

                              <td className="px-3 py-3 text-xs font-normal">
                                {productsToRender.map((product, index) => (
                                  <p
                                    key={index}
                                    className="font-normal text-black text-xs"
                                  >
                                    {product?.sku_name}
                                  </p>
                                ))}
                              </td>

                              <td className="px-3 py-3 text-xs font-normal">
                                {productsToRender.map((product, index) => (
                                  <p
                                    key={index}
                                    className="font-normal text-black text-xs"
                                  >
                                    {product?.qty} Qty
                                  </p>
                                ))}

                                {products.length > 3 && (
                                  <button
                                    onClick={() =>
                                      handleToggleShowAll(bom?.bom_id)
                                    }
                                    className="font-medium text-blue-500 text-xs"
                                  >
                                    {shouldShowAll
                                      ? "View Less"
                                      : "View More"}
                                  </button>
                                )}
                              </td>

                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                <Toggle
                                  onToggle={(_state: boolean) => {}}
                                  checked={bom?.status}
                                  disabled={true}
                                />
                              </td>

                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal cursor-pointer",
                                  isSingle &&
                                    "rounded-tr-2xl rounded-br-2xl",
                                  isFirst &&
                                    !isSingle &&
                                    "rounded-tr-2xl",
                                  isLast &&
                                    !isSingle &&
                                    "rounded-br-2xl",
                                )}
                                onClick={() =>
                                  handelBomEdit(bom?.bom_id)
                                }
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BOMSetupListing;